/**
 * Wall-clock formatting.
 *
 * The API returns an instant plus a resolved IANA zone, and both are needed. `timestamptz` stores
 * an instant and discards the original offset, so "we leave at 5pm Friday" cannot be recovered from
 * the instant alone — it has to be rendered *in the unit's zone*, not the viewer's.
 *
 * That distinction matters in practice: a parent travelling, or in a neighbouring zone, must still
 * read the departure time the troop meant. Formatting in the browser's local zone would quietly
 * show them the wrong hour.
 */

export function formatInZone(
  iso: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions = {},
): string {
  const shape: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...options,
  };

  try {
    return new Intl.DateTimeFormat('en-US', { timeZone, ...shape }).format(new Date(iso));
  } catch {
    // `Intl.DateTimeFormat` throws a RangeError on a zone it does not recognise -- including the
    // empty string, which is what an event carries when it does NOT override its unit's zone. A
    // server that forgets to resolve that would otherwise take down every screen rendering the
    // row, which is exactly what happened: one un-coalesced column blanked the conversations page
    // for a whole troop.
    //
    // Falling back to the viewer's own zone shows a time that may be an hour out, which is a
    // smaller wrong than a page that does not render. The server resolving the zone properly is
    // still the fix; this only decides what a bug looks like.
    return new Intl.DateTimeFormat('en-US', shape).format(new Date(iso));
  }
}

export function formatTimeInZone(iso: string, timeZone: string): string {
  return formatInZone(iso, timeZone, {
    weekday: undefined,
    month: undefined,
    day: undefined,
  });
}

export function formatDayInZone(iso: string, timeZone: string): string {
  return formatInZone(iso, timeZone, {
    weekday: 'long',
    hour: undefined,
    minute: undefined,
  });
}

/**
 * An instant back to the reading a `datetime-local` input wants — `2026-10-30T17:00` — in the
 * event's zone rather than the browser's.
 *
 * This is the inverse of what the API does on the way in, and it has to happen somewhere: an edit
 * form is prefilled with what the leader typed, and all the record keeps is the instant plus the
 * zone. Formatting with the browser's own zone would open the form on a departure time an hour
 * out, and saving it would then make that wrong time the record.
 */
export function toWallClockInZone(iso: string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    // Without this, midnight formats as hour "24" in en-US and the input silently rejects it.
    hourCycle: 'h23',
  }).formatToParts(new Date(iso));

  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '00';

  return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}`;
}

/**
 * When something arrived: always a clock time, with the date in front of it only when that was not
 * today. `9:16 AM` for today, `Aug 25, 9:16 AM` before that, and the year as well across a year
 * boundary so last August does not read identically to this one.
 *
 * **Deliberately not relative.** "2h ago" makes the reader do arithmetic to answer the question
 * they actually have — *when did this arrive* — and it goes stale on screen, because nothing
 * re-renders a thread just because a minute passed. It was also hiding the time completely on
 * anything over a day old: a whole day of conversation collapsed to "Aug 25", leaving the order
 * within it inferable only from position.
 *
 * **This renders in the viewer's own zone, and that is the opposite of what {@link formatInZone}
 * does — on purpose.** These are instants: *when did this message arrive* is a question about the
 * reader, wherever they are standing. An event's start time is a wall-clock reading and belongs to
 * the troop, which is why it resolves through `Unit.TimeZoneId`. Do not "correct" this one to match
 * the other; they answer different questions.
 */
export function formatTimestamp(iso: string): string {
  const when = new Date(iso);

  // An unparseable date makes Intl throw a RangeError, which unmounts the React tree and presents
  // as a blank page with nothing in the console -- the same failure an unrecognised zone caused
  // above. Rendering nothing is a much smaller wrong than rendering no screen.
  if (Number.isNaN(when.getTime())) {
    return '';
  }

  const now = new Date();

  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(when);

  const sameDay =
    when.getFullYear() === now.getFullYear() &&
    when.getMonth() === now.getMonth() &&
    when.getDate() === now.getDate();

  if (sameDay) {
    return time;
  }

  const date = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    ...(when.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  }).format(when);

  return `${date}, ${time}`;
}

/** True when the zone the event is in differs from the viewer's own. */
export function isForeignZone(timeZone: string): boolean {
  return Intl.DateTimeFormat().resolvedOptions().timeZone !== timeZone;
}
